import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { AppHeader, type AppHeaderProps } from './AppHeader'

interface PageLayoutProps extends AppHeaderProps {
  /** Merged onto `<main>`; pass a `space-y-*`/layout class to override the default spacing. */
  className?: string
  children: ReactNode
  /**
   * Fill the viewport height: header stays fixed, `<main>` stretches to fill
   * the remaining space so children can manage their own scroll (e.g. the
   * schedule timetable grid). Default is page-level scroll.
   */
  fillViewport?: boolean
}

export function PageLayout({
  back,
  title,
  leading,
  action,
  hideNav,
  showClock,
  dayLabel,
  hideOnMobile,
  className,
  children,
  fillViewport,
}: PageLayoutProps) {
  return (
    <div
      className={cn(
        'bg-background',
        fillViewport
          ? 'nav-fill-viewport flex flex-col overflow-hidden'
          : 'min-h-full',
      )}
    >
      <AppHeader
        back={back}
        title={title}
        leading={leading}
        action={action}
        hideNav={hideNav}
        showClock={showClock}
        dayLabel={dayLabel}
        hideOnMobile={hideOnMobile}
      />
      {hideOnMobile && <div aria-hidden className="h-[env(safe-area-inset-top)] md:hidden" />}
      <main
        className={cn(
          fillViewport
            ? 'flex-1 min-h-0 flex flex-col'
            : 'mx-auto max-w-2xl px-4 pt-4 pb-6 space-y-3',
          className,
        )}
      >
        {children}
      </main>
    </div>
  )
}
